import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AppError, isAppError, extractErrorCode, ErrorContext } from '@/lib/app-error';
import { ERROR_CATALOG, ErrorSeverity } from '@/lib/error-catalog';
import { captureError } from '@/lib/sentry';

interface ErrorHandlerOptions {
  showToast?: boolean;
  logToDatabase?: boolean;
  context?: ErrorContext;
}

export function useErrorHandler() {
  const { toast } = useToast();

  const logErrorToDatabase = useCallback(async (error: AppError) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      let appUserId: string | null = null;
      
      if (userData?.user?.id) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('id')
          .eq('user_id', userData.user.id)
          .single();
        appUserId = appUser?.id || null;
      }

      await supabase.from('error_logs').insert({
        error_code: error.code,
        error_message: error.message,
        error_context: error.context as Record<string, unknown>,
        stack_trace: error.stack,
        user_id: appUserId,
        severity: error.severity,
        source: 'frontend',
        url: window.location.href,
        user_agent: navigator.userAgent,
      });
    } catch (logError) {
      console.error('Failed to log error to database:', logError);
    }
  }, []);

  const handleError = useCallback(
    async (
      error: unknown,
      options: ErrorHandlerOptions = { showToast: true, logToDatabase: true }
    ): Promise<AppError> => {
      let appError: AppError;

      if (isAppError(error)) {
        appError = error;
      } else if (error instanceof Error) {
        const code = extractErrorCode(error);
        appError = AppError.fromError(error, code, options.context);
      } else {
        appError = new AppError('E2001', {
          message: String(error),
          context: options.context,
        });
      }

      // Log to console for debugging
      console.error(`[${appError.code}] ${appError.message}`, appError.context);

      // Report to Sentry
      captureError(appError.originalError || appError, {
        errorCode: appError.code,
        severity: appError.severity,
        ...appError.context,
      });

      // Show toast notification
      if (options.showToast !== false) {
        const variant = appError.severity === 'warning' ? 'default' : 'destructive';
        toast({
          title: `Error ${appError.code}`,
          description: appError.userMessage,
          variant,
        });
      }

      // Log to database
      if (options.logToDatabase !== false) {
        await logErrorToDatabase(appError);
      }

      return appError;
    },
    [toast, logErrorToDatabase]
  );

  const createError = useCallback(
    (code: string, context?: ErrorContext): AppError => {
      return new AppError(code, { context });
    },
    []
  );

  const throwError = useCallback(
    (code: string, context?: ErrorContext): never => {
      throw new AppError(code, { context });
    },
    []
  );

  return {
    handleError,
    createError,
    throwError,
    logErrorToDatabase,
  };
}

// Standalone function for use outside of React components
export async function logError(
  code: string,
  message: string,
  context?: ErrorContext,
  severity: ErrorSeverity = 'error'
) {
  try {
    const { data: userData } = await supabase.auth.getUser();
    let appUserId: string | null = null;
    
    if (userData?.user?.id) {
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('user_id', userData.user.id)
        .single();
      appUserId = appUser?.id || null;
    }

    await supabase.from('error_logs').insert({
      error_code: code,
      error_message: message,
      error_context: context as Record<string, unknown>,
      user_id: appUserId,
      severity,
      source: 'frontend',
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
  } catch (logError) {
    console.error('Failed to log error:', logError);
  }
}
